import { FC, ReactNode, useState } from 'react';

import { clsx } from 'clsx';
import { Pencil, Trash2 } from 'lucide-react';

import { CardmarketProductLink } from '../../../../components/shared/CardmarketProductLink';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import {
    CartonProduct,
    OfferPhotoUpload,
    OfferProperty,
} from '../../../../types';

const DEFAULT_PX_OFFSET = 16;

interface Props {
    product: CartonProduct | null;
    importName?: string | null;
    properties: OfferProperty[];
    propertySet: Record<string, string>;
    amount: string | number;
    price: string | number;
    photos?: OfferPhotoUpload[];
    sourceCode?: string | null;
    statusLabel?: string | null;
    statusVariant?: 'success' | 'warning' | 'info' | 'neutral';
    helperText?: string | null;
    isEditing?: boolean;
    editorContent?: ReactNode;
    onEdit?: () => void;
    onRemove?: () => void;
    editAriaLabel?: string;
    removeAriaLabel?: string;
}

export const OfferDraftCard: FC<Props> = ({
    product,
    importName,
    properties,
    propertySet,
    amount,
    price,
    photos = [],
    sourceCode,
    statusLabel,
    statusVariant = 'neutral',
    helperText,
    isEditing = false,
    editorContent,
    onEdit,
    onRemove,
    editAriaLabel = 'Редагувати товар у черзі',
    removeAriaLabel = 'Видалити товар із черги',
}) => {
    const [preview, setPreview] = useState<{
        product: CartonProduct;
        offset: {
            x: number;
            y: number;
        };
    } | null>(null);
    const parsedPrice = Number.parseFloat(price.toString());
    const displayPrice =
        Number.isFinite(parsedPrice) && parsedPrice > 0
            ? parsedPrice.toFixed(2)
            : null;

    const showProductImage = (
        previewProduct: CartonProduct,
        offset: { x: number; y: number },
    ) => {
        setPreview({
            product: previewProduct,
            offset,
        });
    };

    return (
        <>
            {preview && (
                <img
                    data-testid="product-image-preview"
                    className="pointer-events-none fixed z-[130] h-[350px] w-[250px] rounded-2xl"
                    style={{
                        top: `${preview.offset.y + DEFAULT_PX_OFFSET}px`,
                        left: `${preview.offset.x + DEFAULT_PX_OFFSET}px`,
                    }}
                    src={preview.product.image}
                    alt={preview.product.image_alt}
                />
            )}
            <div
                className={clsx(
                    'overflow-hidden rounded-xl border transition-[border-color,box-shadow]',
                    {
                        'border-primary shadow-[0_0_0_2px_hsl(var(--primary)/0.28)]':
                            isEditing,
                    },
                )}
                data-testid="offer-draft-card"
            >
                <div className="flex items-stretch gap-3 p-3">
                    <div className="relative h-20 w-16 shrink-0 sm:h-24 sm:w-20">
                        {product ? (
                            <img
                                src={product.image}
                                alt={product.image_alt}
                                className="h-full w-full object-contain"
                                data-testid="offer-draft-product-image"
                                loading="lazy"
                                onTouchStart={(event) => {
                                    event.stopPropagation();
                                    showProductImage(product, {
                                        x: event.touches[0].clientX,
                                        y: event.touches[0].clientY,
                                    });
                                }}
                                onMouseMove={(event) =>
                                    showProductImage(product, {
                                        x: event.clientX,
                                        y: event.clientY,
                                    })
                                }
                                onMouseOut={() => {
                                    setPreview(null);
                                }}
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center border border-dashed border-muted-foreground/40 px-2 text-center text-xs text-muted-foreground">
                                Товар не обрано
                            </div>
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 space-y-2">
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                    <p className="truncate font-medium">
                                        {product?.name ??
                                            importName ??
                                            'Потрібно обрати товар'}
                                    </p>
                                    {product && (
                                        <Badge data-testid="offer-draft-expansion-tag">
                                            {product.expansion_tag}
                                        </Badge>
                                    )}
                                    {product && (
                                        <Badge
                                            variant="outline"
                                            data-testid="offer-draft-language"
                                        >
                                            {product.language}
                                        </Badge>
                                    )}
                                    {statusLabel && (
                                        <Badge variant={statusVariant}>
                                            {statusLabel}
                                        </Badge>
                                    )}
                                    {product?.cardmarket_url && (
                                        <CardmarketProductLink
                                            href={product.cardmarket_url}
                                        />
                                    )}
                                </div>
                                {sourceCode && (
                                    <p className="text-xs text-muted-foreground">
                                        Код: {sourceCode}
                                    </p>
                                )}
                                {photos.length > 0 && (
                                    <div className="flex items-center gap-1.5">
                                        {photos.slice(0, 4).map((photo) => (
                                            <div
                                                key={photo.signed_id}
                                                className="h-8 w-8 overflow-hidden rounded-md border bg-muted"
                                            >
                                                {photo.preview_url && (
                                                    <img
                                                        src={photo.preview_url}
                                                        alt={photo.filename}
                                                        className="h-full w-full object-cover"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                        <span className="text-xs text-muted-foreground">
                                            {photos.length} фото
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex shrink-0 gap-1">
                                {onEdit && (
                                    <Button
                                        type="button"
                                        variant={
                                            isEditing
                                                ? 'outlinePrimary'
                                                : 'ghost'
                                        }
                                        size="icon"
                                        className="h-9 w-9"
                                        onClick={onEdit}
                                        aria-label={editAriaLabel}
                                        aria-expanded={isEditing}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                                {onRemove && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9"
                                        onClick={onRemove}
                                        aria-label={removeAriaLabel}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div className="space-y-1 text-sm text-muted-foreground">
                            {properties.map((property) => {
                                const selectedValue =
                                    propertySet[property.slug];
                                if (!selectedValue) return null;

                                const displayValue =
                                    property.kind === 'collection'
                                        ? property.offer_property_options.find(
                                              (option) =>
                                                  option.slug === selectedValue,
                                          )?.name || selectedValue
                                        : selectedValue;

                                return (
                                    <p
                                        key={`${
                                            product?.id ?? 'draft'
                                        }-${property.slug}`}
                                        data-testid={`offer-draft-property-${property.slug}`}
                                        className="min-w-0 [overflow-wrap:anywhere] break-words"
                                    >
                                        {property.name}: {displayValue}
                                    </p>
                                );
                            })}
                            <p>Кількість: {amount}</p>
                            <p>
                                Ціна за одиницю:{' '}
                                {displayPrice
                                    ? `₴${displayPrice}`
                                    : 'Не заповнено'}
                            </p>
                            {helperText && (
                                <p className="text-xs text-amber-600">
                                    {helperText}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {editorContent && (
                    <div
                        className="border-t bg-muted/20 p-3 sm:p-4"
                        data-testid="offer-draft-editor"
                    >
                        {editorContent}
                    </div>
                )}
            </div>
        </>
    );
};
